import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useABTest } from '../../hooks/useABTest';

export default function ContactPage() {
  // A/B Test : Formulaire de contact (court vs détaillé)
  const contactFormTest = useABTest('contact-form-test');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    agentType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Variante A/B : Formulaire court ou détaillé
  const showDetailedForm = contactFormTest.config?.showDetailedForm || false;
  const formTitle = contactFormTest.config?.formTitle || 'Envoyez-nous un message';

  // URL du formulaire
  const submitAddr = 'https://readdy.ai/api/form/d5tr8kb0o41p730738j0';

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Contactez-Nous Paris - AgentIA Pro | Support Client 24/7 Agents IA Démo Gratuite';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    setMeta('description', 'Contactez notre équipe d\'experts en intelligence artificielle à Paris. Support client 24/7 pour agents IA, démo personnalisée gratuite, réponse sous 24h. Téléphone : 06 76 77 39 01. Transformez votre entreprise avec nos agents IA intelligents.');
    setMeta('keywords', 'contact AgentIA Pro Paris, support client IA Paris, démo gratuite agent IA, assistance technique agents IA, consultation IA Paris, expert intelligence artificielle Paris');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`);
      document.head.appendChild(canonical);
    }

    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) { lastModified.setAttribute('content', new Date().toISOString().split('T')[0]); }
    else {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
      document.head.appendChild(lastModified);
    }

    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact` },
      { property: 'og:title', content: 'Contactez-Nous - AgentIA Pro Paris | Support Agents IA' },
      { property: 'og:description', content: 'Contactez notre équipe d\'experts en agents IA à Paris. Réponse sous 24h, démo gratuite disponible.' },
    ];
    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) { tag.setAttribute('content', content); }
      else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Track conversion pour l'A/B test
        contactFormTest.trackConversion(true, { 
          formType: showDetailedForm ? 'detailed' : 'short',
          variant: contactFormTest.variantId 
        });
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: '',
          agentType: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-white"
    >
      {/* Structured Data for Contact Page */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact AgentIA Pro Paris",
          "description": "Contactez notre équipe d'experts en intelligence artificielle à Paris pour transformer votre entreprise avec des agents IA",
          "url": "https://agentia.pro/contact",
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": "AgentIA Pro",
            "telephone": "+33-1-23-45-67-89",
            "email": "contact@agentia.pro",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "123 Avenue des Champs-Élysées",
              "addressLocality": "Paris",
              "addressRegion": "Île-de-France",
              "postalCode": "75008",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "48.856614",
              "longitude": "2.352222"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "18:00"
            }
          }
        })}
      </script>

      {/* FAQ Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Quel est le délai de réponse moyen pour les agents IA ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Notre équipe s'engage à répondre à toutes les demandes concernant nos agents IA dans un délai de 24 heures ouvrées. Pour les demandes urgentes sur les agents IA conversationnels, n'hésitez pas à nous appeler directement au +33 1 23 45 67 89."
              }
            },
            {
              "@type": "Question",
              "name": "Proposez-vous des démos personnalisées d'agents IA ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Oui, nous proposons des démonstrations personnalisées de nos agents IA intelligents adaptées à votre secteur d'activité et vos besoins spécifiques. Nos agents IA conversationnels peuvent être testés gratuitement."
              }
            },
            {
              "@type": "Question",
              "name": "Comment se déroule l'intégration des agents IA ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Notre processus d'intégration des agents IA est simple et guidé. Après votre demande, un expert vous contactera pour analyser vos besoins en agents IA, puis nous vous accompagnons étape par étape dans la mise en place de votre agent IA intelligent."
              }
            },
            {
              "@type": "Question",
              "name": "Quels sont les tarifs des agents IA ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Nos tarifs pour les agents IA varient selon vos besoins et le type d'agent IA choisi. Consultez notre page tarifs ou contactez-nous pour obtenir un devis personnalisé adapté à votre entreprise pour nos agents IA conversationnels."
              }
            }
          ]
        })}
      </script>

      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              <strong>Contactez-Nous Paris</strong> - Support Expert Agents IA
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Notre <strong>équipe d'experts en agents IA</strong> est prête à vous accompagner dans votre <strong>transformation digitale avec agents IA conversationnels</strong>. Réponse garantie sous 24h pour tous vos besoins en <strong>agents IA intelligents</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="jean.dupont@entreprise.fr"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                      Sujet
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm cursor-pointer"
                    >
                      <option value="">Sélectionnez un sujet</option>
                      <option value="demo">Demande de démo</option>
                      <option value="pricing">Question sur les tarifs</option>
                      <option value="technical">Support technique</option>
                      <option value="partnership">Partenariat</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                      placeholder="Décrivez votre projet ou votre besoin..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.message.length}/500 caractères
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm font-medium">
                        ✓ Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm font-medium">
                        ✗ Une erreur est survenue. Veuillez vérifier vos informations et réessayer.
                      </p>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Informations de contact</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Email</h4>
                      <p className="text-white/90 text-sm">theo.lalanne0@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Téléphone</h4>
                      <p className="text-white/90 text-sm">06 76 77 39 01</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Adresse</h4>
                      <p className="text-white/90 text-sm">
                        37 la grange<br />
                        33550 Capian, France
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Horaires</h4>
                      <p className="text-white/90 text-sm">
                        Lundi - Vendredi : 18h - 21h30<br />
                        Samedi - Dimanche : Fermé
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Réponse rapide</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Notre équipe s'engage à vous répondre dans les <strong>24 heures</strong> suivant la réception de votre message.
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-shield-check-line text-2xl text-teal-500"></i>
                  <span>Vos données sont sécurisées et confidentielles</span>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Suivez-nous</h3>
                <div className="flex gap-4">
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-500 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-linkedin-fill text-xl"></i>
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-500 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-twitter-x-fill text-xl"></i>
                  </a>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-500 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-500 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-instagram-fill text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Questions Fréquentes
          </h2>
          <div className="space-y-4">
            <details className="bg-white rounded-lg shadow-sm p-6 cursor-pointer group">
              <summary className="font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>Quel est le délai de réponse moyen ?</span>
                <i className="ri-arrow-down-s-line text-xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 text-sm leading-relaxed">
                Notre équipe s'engage à répondre à toutes les demandes dans un délai de 24 heures ouvrées. Pour les demandes urgentes, n'hésitez pas à nous appeler directement.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm p-6 cursor-pointer group">
              <summary className="font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>Proposez-vous des démos personnalisées ?</span>
                <i className="ri-arrow-down-s-line text-xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 text-sm leading-relaxed">
                Oui, nous proposons des démonstrations personnalisées de nos agents IA adaptées à votre secteur d'activité et vos besoins spécifiques. Sélectionnez "Demande de démo" dans le formulaire ci-dessus.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm p-6 cursor-pointer group">
              <summary className="font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>Comment se déroule l'intégration ?</span>
                <i className="ri-arrow-down-s-line text-xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 text-sm leading-relaxed">
                Notre processus d'intégration est simple et guidé. Après votre demande, un expert vous contactera pour analyser vos besoins, puis nous vous accompagnons étape par étape dans la mise en place de votre agent IA.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm p-6 cursor-pointer group">
              <summary className="font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>Quels sont les tarifs ?</span>
                <i className="ri-arrow-down-s-line text-xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 text-sm leading-relaxed">
                Nos tarifs varient selon vos besoins et le type d'agent IA choisi. Consultez notre page tarifs ou contactez-nous pour obtenir un devis personnalisé adapté à votre entreprise.
              </p>
            </details>
          </div>
        </div>
      </section>

      <Footer />
    </motion.div>
  );
}
